import CreditCardIcon from "@mui/icons-material/CreditCard";
import { IconButton, Tooltip } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import PageRoutes from "../../../utils/constants";
import { useSubscriptionStatus } from "../../Contex/SubscriptionStatusContex";
import { buildPayUrl, shouldShowPaymentAlert } from "../../../utils/paymentRecovery";

/**
 * HTPTV-695: compact header indicator for a subscriber whose renewal is failing
 * (past_due / unpaid / incomplete). It replaces the full-width HTPTV-738 banner:
 * same audience and copy, but it sits beside the search bar, explains itself on
 * hover, and links straight to the fix instead of pushing every page down.
 *
 * There is no dismiss: a 24px icon is not intrusive enough to need an escape
 * hatch, and a dismissible indicator lets a user hide a billing problem from
 * themselves indefinitely.
 */
const PaymentAlert = () => {
  const { subscription, pendingPayment } = useSubscriptionStatus();
  const { pathname } = useLocation();

  // Don't nag on the checkout page itself. The user is already doing the thing
  // the indicator asks for, and the link would point at the current page.
  if (pathname.startsWith("/payment")) return null;
  if (!shouldShowPaymentAlert(subscription)) return null;

  // With an outstanding invoice there is nothing to "update": an incomplete
  // signup has no card at all. Send those users to checkout; send everyone else
  // to the account page to replace the card that failed.
  const payUrl = buildPayUrl(pendingPayment);
  const label = payUrl
    ? "Your subscription isn't active yet. Finish paying to start watching."
    : "Your payment didn't go through. Update your card to keep watching.";

  return (
    <Tooltip title={label} arrow>
      <IconButton
        component={RouterLink}
        to={payUrl ?? PageRoutes.AccountPage}
        aria-label={label}
        sx={{ color: "warning.main" }}
      >
        <CreditCardIcon />
      </IconButton>
    </Tooltip>
  );
};

export default PaymentAlert;
